import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Settings, Moon, Sun, LogOut, RefreshCw, Mail, Wallet, RefreshCcw, Download, CreditCard, List, Globe, Unlink, Check, AlertTriangle, Target, FileText, Receipt, StickyNote } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Tooltip from '@radix-ui/react-tooltip';
import UserAvatar from '../UserAvatar';
import Logo from '../Logo/Logo';
import WaveyIcon from '../Icons/waveyIcon';
import AppModal from '../Modal/AppModal';


interface SidebarProps {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  language: 'en' | 'es';
  setLanguage: (lang: 'en' | 'es') => void;
  t: any;
  onLogout: () => void;
  currentUser?: {
    name?: string;
    lastName?: string;
    email: string;
    profileImage?: string;
  } | null;
  // Dashboard-specific props
  hasPaidExpenses?: boolean;
  onResetAllPaid?: () => void;
  onExportClick?: () => void;
  onEmailReport?: () => void;
  onAiAdvisor?: () => void;
  userTier?: 'free' | 'standard' | 'premium';
  hasLinkedBanks?: boolean;
  plaidAccountsCount?: number;
  plaidAccounts?: any;
  onBankSync?: () => void;
  isSyncing?: boolean;
  onLinkBank?: () => void;
  onUnlinkBank?: (itemId: string) => void;
  onUpgradeToPremium?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  theme,
  toggleTheme,
  language,
  setLanguage,
  t,
  onLogout,
  currentUser,
  hasPaidExpenses = false,
  onResetAllPaid,
  onExportClick,
  onEmailReport,
  // onAiAdvisor, // Removed unused parameter
  userTier = 'free',
  hasLinkedBanks = false,
  plaidAccountsCount = 0,
  plaidAccounts,
  onBankSync,
  isSyncing = false,
  onLinkBank,
  onUnlinkBank,
  onUpgradeToPremium,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [languageAnchorEl, setLanguageAnchorEl] = React.useState<null | HTMLElement>(null);
  const [bankMenuAnchorEl, setBankMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [unlinkConfirm, setUnlinkConfirm] = React.useState<{ open: boolean; itemId: string; bankName: string }>({ open: false, itemId: '', bankName: '' });

  const isDashboard = location.pathname === '/dashboard';
  const isSettings = location.pathname === '/settings';
  const languageMenuOpen = Boolean(languageAnchorEl);
  const bankMenuOpen = Boolean(bankMenuAnchorEl);

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchorEl(null);
  };

  const handleLanguageSelect = (lang: 'en' | 'es') => {
    setLanguage(lang);
    handleLanguageClose();
  };

  const handleBankMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setBankMenuAnchorEl(event.currentTarget);
  };

  const handleBankMenuClose = () => {
    setBankMenuAnchorEl(null);
  };

  const handleUnlinkBank = (itemId: string, bankName: string) => {
    setUnlinkConfirm({ open: true, itemId, bankName });
    handleBankMenuClose();
  };

  const isSubscription = location.pathname === '/subscription';
  const isWaveyAI = location.pathname === '/wavey-ai';
  const isExpensesList = location.pathname === '/expenses-list';
  const isPlanner = location.pathname === '/planner';
  const isReports = location.pathname === '/reports';
  const isReimbursements = location.pathname === '/reimbursements';
  const isNotes = location.pathname === '/notes';

  return (
    <>
    <aside className={`fixed top-0 left-0 h-screen w-[280px] z-[1000] hidden md:flex flex-col overflow-y-auto overflow-x-hidden backdrop-blur-xl border-r shadow-lg transition-all duration-300 scrollbar-thin ${
      theme === 'dark' 
        ? 'scrollbar-thumb-white/20 scrollbar-track-transparent' 
        : 'scrollbar-thumb-gray-300 scrollbar-track-gray-100'
    } ${theme === 'dark' ? 'bg-[#1c1c1e]/80 border-white/10' : 'bg-white/90 border-black/10'}`}>
      <div className="flex flex-col h-full p-4">
        {/* Logo Section */}
        <div className="pb-4 mb-1 cursor-pointer hover:scale-[1.02] active:scale-[0.98] transition-transform">
          <Logo
            theme={theme}
            title={t.title || 'WaveBudget'}
            onClick={() => navigate('/dashboard')}
          />
        </div>

        {/* Navigation Section */}
        <nav className={`flex-1 flex flex-col gap-4 overflow-y-auto scrollbar-thin px-3 ${
          theme === 'dark' 
            ? 'scrollbar-thumb-white/15 scrollbar-track-transparent' 
            : 'scrollbar-thumb-gray-300 scrollbar-track-gray-100'
        }`}>
          <div className="flex flex-col gap-2">
              <span className={`text-[11px] font-semibold uppercase tracking-wider px-3 pb-1 opacity-70 ${
                theme === 'dark' ? 'text-white/40' : 'text-black/40'
              }`}>
                {t.sidebar?.navigation || 'Navigation'}
              </span>

            <button
              onClick={() => navigate('/dashboard')}
              className={`w-full flex items-center justify-start gap-2 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                theme === 'dark' 
                  ? 'text-white/60 hover:bg-white/5 hover:text-white' 
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              } ${
                isDashboard ? 'bg-gradient-to-r from-primary to-primary-dark text-white font-semibold shadow-lg shadow-primary/30 border-transparent' : ''
              }`}
            >
              <span className="flex items-center justify-center w-5 flex-shrink-0"><LayoutDashboard size={20} /></span>
              <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.navbar?.dashboard || 'Dashboard'}</span>
            </button>
            <button
              onClick={() => navigate('/expenses-list')}
              className={`w-full flex items-center justify-start gap-2 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                theme === 'dark' 
                  ? 'text-white/60 hover:bg-white/5 hover:text-white' 
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              } ${
                isExpensesList ? 'bg-gradient-to-r from-primary to-primary-dark text-white font-semibold shadow-lg shadow-primary/30 border-transparent' : ''
              }`}
            >
              <span className="flex items-center justify-center w-5 flex-shrink-0"><List size={20} /></span>
              <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.navbar?.expenses || 'Expenses'}</span>
            </button>
            <button
              onClick={() => navigate('/planner')}
              className={`w-full flex items-center justify-start gap-2 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                theme === 'dark'
                  ? 'text-white/60 hover:bg-white/5 hover:text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              } ${
                isPlanner ? 'bg-gradient-to-r from-primary to-primary-dark text-white font-semibold shadow-lg shadow-primary/30 border-transparent' : ''
              }`}
            >
              <span className="flex items-center justify-center w-5 flex-shrink-0"><Target size={20} /></span>
              <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.navbar?.planner || 'Planner'}</span>
            </button>
            <button
              onClick={() => navigate('/reports')}
              className={`w-full flex items-center justify-start gap-2 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                theme === 'dark'
                  ? 'text-white/60 hover:bg-white/5 hover:text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              } ${
                isReports ? 'bg-gradient-to-r from-primary to-primary-dark text-white font-semibold shadow-lg shadow-primary/30 border-transparent' : ''
              }`}
            >
              <span className="flex items-center justify-center w-5 flex-shrink-0"><FileText size={20} /></span>
              <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.sidebar?.reports ?? 'Reports'}</span>
            </button>
            <button
              onClick={() => navigate('/reimbursements')}
              className={`w-full flex items-center justify-start gap-2 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                theme === 'dark'
                  ? 'text-white/60 hover:bg-white/5 hover:text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              } ${
                isReimbursements ? 'bg-gradient-to-r from-primary to-primary-dark text-white font-semibold shadow-lg shadow-primary/30 border-transparent' : ''
              }`}
            >
              <span className="flex items-center justify-center w-5 flex-shrink-0"><Receipt size={20} /></span>
              <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.sidebar?.reimbursements ?? 'Reimbursements'}</span>
            </button>
            <button
              onClick={() => navigate('/notes')}
              className={`w-full flex items-center justify-start gap-2 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                theme === 'dark'
                  ? 'text-white/60 hover:bg-white/5 hover:text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              } ${
                isNotes ? 'bg-gradient-to-r from-primary to-primary-dark text-white font-semibold shadow-lg shadow-primary/30 border-transparent' : ''
              }`}
            >
              <span className="flex items-center justify-center w-5 flex-shrink-0"><StickyNote size={20} /></span>
              <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.sidebar?.notes ?? 'Notes'}</span>
            </button>
            <button
              onClick={() => navigate('/settings')}
              className={`w-full flex items-center justify-start gap-2 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                theme === 'dark'
                  ? 'text-white/60 hover:bg-white/5 hover:text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              } ${
                isSettings ? 'bg-gradient-to-r from-primary to-primary-dark text-white font-semibold shadow-lg shadow-primary/30 border-transparent' : ''
              }`}
            >
              <span className="flex items-center justify-center w-5 flex-shrink-0"><Settings size={20} /></span>
              <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.navbar?.settings || 'Settings'}</span>
            </button>

            <button
              onClick={() => navigate('/subscription')}
              className={`w-full flex items-center justify-start gap-2 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                theme === 'dark' 
                  ? 'text-white/60 hover:bg-white/5 hover:text-white' 
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              } ${
                isSubscription ? 'bg-gradient-to-r from-primary to-primary-dark text-white font-semibold shadow-lg shadow-primary/30 border-transparent' : ''
              }`}
            >
              <span className="flex items-center justify-center w-5 flex-shrink-0"><CreditCard size={20} /></span>
              <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.navbar?.subscription || 'Subscription'}</span>
            </button>
          </div>

          <div className={`h-px my-1 ${theme === 'dark' ? 'bg-white/10' : 'bg-gray-200'}`} />

          {/* Tools Section */}
          <div className="flex flex-col gap-2">
            <span className={`text-[11px] font-semibold uppercase tracking-wider px-3 pb-1 opacity-70 ${
              theme === 'dark' ? 'text-white/40' : 'text-black/40'
            }`}>
              {t.sidebar?.tools || 'Tools'}
            </span>

            {/* AI Advisor */}
            <button
              onClick={() => navigate('/wavey-ai')}
              className={`w-full flex items-center justify-start gap-2 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                theme === 'dark' 
                  ? 'text-white/60 hover:bg-white/5 hover:text-white' 
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              } ${
                isWaveyAI ? 'bg-gradient-to-r from-primary to-primary-dark text-white font-semibold shadow-lg shadow-primary/30 border-transparent' : ''
              }`}
            >
              <span className="flex items-center justify-center w-5 flex-shrink-0">
                <WaveyIcon style={{ color: isWaveyAI || theme === 'dark' ? 'white' : 'black' }} />
              </span>
              <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.navbar?.waveyAI || 'Wavey AI'}</span>
            </button>

            {/* Bank Integration */}
            {userTier === 'premium' ? (
              hasLinkedBanks ? (
                <>
                  {(isDashboard || isSettings || isExpensesList) && (
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-transparent bg-green-500/10 text-green-400 text-[15px] font-medium cursor-default">
                      <span className="flex items-center justify-center w-5 flex-shrink-0"><Wallet size={20} /></span>
                      <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{plaidAccountsCount} {t.navbar?.banks || 'Banks'}</span>
                    </div>
                  )}
                  {onBankSync && (isDashboard || isSettings || isExpensesList) && (
                    <button
                      onClick={onBankSync}
                      disabled={isSyncing}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 disabled:opacity-50 disabled:cursor-not-allowed ${
                        theme === 'dark' 
                          ? 'text-white/60 hover:bg-white/5 hover:text-white' 
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <span className="flex items-center justify-center w-5 flex-shrink-0">
                        <RefreshCcw size={20} style={{ animation: isSyncing ? 'spin 1s linear infinite' : 'none' }} />
                      </span>
                      <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
                        {isSyncing ? t.navbar?.syncing || 'Syncing...' : t.navbar?.sync || 'Sync'}
                      </span>
                    </button>
                  )}
                  {onUnlinkBank && (isDashboard || isSettings || isExpensesList) && plaidAccounts?.accounts?.length > 0 && (
                    <DropdownMenu.Root open={bankMenuOpen} onOpenChange={(open) => { if (!open) setBankMenuAnchorEl(null); }}>
                      <DropdownMenu.Trigger asChild>
                        <button
                          onClick={handleBankMenuClick}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-red text-[15px] font-medium cursor-pointer transition-all duration-300 hover:bg-red/10 hover:translate-x-1"
                        >
                          <span className="flex items-center justify-center w-5 flex-shrink-0">
                            <Unlink size={20} />
                          </span>
                          <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
                            {t.plaidLink?.unlinkBank || 'Unlink Bank'}
                          </span>
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content className="z-[1100] min-w-[160px] rounded-xl bg-[#1a1a1a] border border-white/10 p-1 shadow-xl animate-slide-up" sideOffset={4}>
                          {plaidAccounts?.accounts?.map((bank: any) => (
                            <DropdownMenu.Item
                              key={bank.itemId}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red hover:bg-white/10 cursor-pointer outline-none"
                              onSelect={() => handleUnlinkBank(bank.itemId, bank.bankName)}
                            >
                              <Unlink size={16} style={{ color: '#FF3B30' }} />
                              {bank.bankName}
                            </DropdownMenu.Item>
                          ))}
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  )}
                </>
              ) : (
                (isDashboard || isSettings || isExpensesList) && (
                  <button
                    onClick={onLinkBank || (() => navigate('/dashboard'))}
                    className={`w-full flex items-center justify-start gap-2 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                      theme === 'dark' 
                        ? 'text-white/60 hover:bg-white/5 hover:text-white' 
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <span className="flex items-center justify-center w-5 flex-shrink-0"><Wallet size={20} /></span>
                    <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.navbar?.linkBank || 'Link Bank'}</span>
                  </button>
                )
              )
            ) : (
              (isDashboard || isSettings || isExpensesList) && (
                <button
                  onClick={onUpgradeToPremium}
                  className={`w-full flex items-center justify-start gap-2 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                    theme === 'dark' 
                      ? 'text-white/60 hover:bg-white/5 hover:text-white' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className="flex items-center justify-center w-5 flex-shrink-0"><Wallet size={20} /></span>
                  <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.navbar?.linkBank || 'Link Bank'}</span>
                </button>
              )
            )}

            {/* Export Data */}
            {onExportClick && (
              <button
                onClick={onExportClick}
                className={`w-full flex items-center justify-start gap-2 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                  theme === 'dark' 
                    ? 'text-white/60 hover:bg-white/5 hover:text-white' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <span className="flex items-center justify-center w-5 flex-shrink-0"><Download size={20} /></span>
                <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.navbar?.export || 'Export'}</span>
              </button>
            )}

            {/* Email Report */}
            {onEmailReport && (
              <button
                onClick={onEmailReport}
                className={`w-full flex items-center justify-start gap-2 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-[15px] font-medium cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                  theme === 'dark' 
                    ? 'text-white/60 hover:bg-white/5 hover:text-white' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <span className="flex items-center justify-center w-5 flex-shrink-0"><Mail size={20} /></span>
                <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.navbar?.email || 'Email Report'}</span>
              </button>
            )}
          </div>

          <div className={`h-px my-1 ${theme === 'dark' ? 'bg-white/10' : 'bg-gray-200'}`} />

          {/* Actions Section */}
          {hasPaidExpenses && onResetAllPaid && (
            <div className="flex flex-col gap-2">
              <span className={`text-[11px] font-semibold uppercase tracking-wider px-3 pb-1 opacity-70 ${
                theme === 'dark' ? 'text-white/40' : 'text-black/40'
              }`}>
                {t.sidebar?.actions || 'Actions'}
              </span>

              <Tooltip.Provider delayDuration={300}>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <button
                      onClick={onResetAllPaid}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-transparent bg-transparent text-orange text-[15px] font-medium cursor-pointer transition-all duration-300 hover:bg-orange/10 hover:translate-x-1"
                    >
                      <span className="flex items-center justify-center w-5 flex-shrink-0"><RefreshCw size={20} /></span>
                      <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{t.navbar?.reset || 'Reset All'}</span>
                    </button>
                  </Tooltip.Trigger>
                  <Tooltip.Content className="z-50 rounded-lg bg-[#1a1a1a] border border-white/10 px-2.5 py-1.5 text-sm text-white shadow-xl" sideOffset={4}>
                    {t.navbar?.resetTooltip || 'Reset all expenses to unpaid to start new billing cycle'}
                  </Tooltip.Content>
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
          )}
        </nav>

        {/* Bottom Section */}
        <div className="p-3 mt-auto">
          <div className={`h-px my-2 ${theme === 'dark' ? 'bg-white/10' : 'bg-gray-200'}`} />

          {/* User Avatar */}
          {currentUser && (
            <div className="flex items-center gap-3 px-3 py-3 rounded-xl mb-3 bg-primary/5 hover:bg-primary/10 transition-colors">
              <UserAvatar
                name={currentUser.name}
                lastName={currentUser.lastName}
                profileImage={currentUser.profileImage}
                size={40}
                fontSize="1rem"
              />
              <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                <div className={`text-[14px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {currentUser.name || currentUser.email}
                  {currentUser.lastName && ` ${currentUser.lastName}`}
                </div>
                <div className={`text-[12px] whitespace-nowrap overflow-hidden text-ellipsis ${
                  theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                }`}>{currentUser.email}</div>
              </div>
            </div>
          )}

          <div className={`flex items-center justify-around gap-2 p-2 rounded-xl ${
            theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'
          }`}>
            {/* Theme Toggle */}
            <Tooltip.Provider delayDuration={300}>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    onClick={toggleTheme}
                    className={`p-2 rounded-lg transition-all active:scale-95 ${
                      theme === 'dark' 
                        ? 'text-white/60 hover:text-white hover:bg-white/10' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {theme === 'light' ? <Moon size={22} /> : <Sun size={22} />}
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content className="z-[1100] rounded-lg bg-[#1a1a1a] border border-white/10 px-2.5 py-1.5 text-sm text-white shadow-xl" sideOffset={4}>
                  {t.navbar?.theme || 'Toggle Theme'}
                </Tooltip.Content>
              </Tooltip.Root>
            </Tooltip.Provider>

            {/* Language Toggle */}
            <DropdownMenu.Root open={languageMenuOpen} onOpenChange={(open) => { if (!open) setLanguageAnchorEl(null); }}>
              <Tooltip.Provider delayDuration={300}>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <DropdownMenu.Trigger asChild>
                      <button
                        className={`p-2 rounded-lg transition-all active:scale-95 ${
                          theme === 'dark' 
                            ? 'text-white/60 hover:text-white hover:bg-white/10' 
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                        }`}
                        onClick={handleLanguageClick}
                      >
                        <Globe size={22} />
                      </button>
                    </DropdownMenu.Trigger>
                  </Tooltip.Trigger>
                  <Tooltip.Content className="z-[1100] rounded-lg bg-[#1a1a1a] border border-white/10 px-2.5 py-1.5 text-sm text-white shadow-xl" sideOffset={4}>
                    {t.navbar?.language || 'Language'}
                  </Tooltip.Content>
                </Tooltip.Root>
              </Tooltip.Provider>
              <DropdownMenu.Portal>
                <DropdownMenu.Content className="z-[1100] min-w-[160px] rounded-xl bg-[#1a1a1a] border border-white/10 p-1 shadow-xl animate-slide-up" sideOffset={4}>
                  <DropdownMenu.Item
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white hover:bg-white/10 cursor-pointer outline-none"
                    onSelect={() => handleLanguageSelect('en')}
                  >
                    <span className="w-4 flex-shrink-0">
                      {language === 'en' && <Check size={16} style={{ color: '#007aff' }} />}
                    </span>
                    English
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white hover:bg-white/10 cursor-pointer outline-none"
                    onSelect={() => handleLanguageSelect('es')}
                  >
                    <span className="w-4 flex-shrink-0">
                      {language === 'es' && <Check size={16} style={{ color: '#007aff' }} />}
                    </span>
                    Español
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>

            {/* Logout */}
            <Tooltip.Provider delayDuration={300}>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    onClick={onLogout}
                    className={`p-2 rounded-lg transition-all active:scale-95 ${
                      theme === 'dark' 
                        ? 'text-white/60 hover:text-red hover:bg-red/10' 
                        : 'text-gray-600 hover:text-red hover:bg-red/10'
                    }`}
                  >
                    <LogOut size={22} />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content className="z-[1100] rounded-lg bg-[#1a1a1a] border border-white/10 px-2.5 py-1.5 text-sm text-white shadow-xl" sideOffset={4}>
                  {t.navbar?.logout || 'Logout'}
                </Tooltip.Content>
              </Tooltip.Root>
            </Tooltip.Provider>
          </div>
        </div>
      </div>
    </aside>

    <AppModal
      open={unlinkConfirm.open}
      onClose={() => setUnlinkConfirm((s) => ({ ...s, open: false }))}
      title={t.plaidLink?.unlinkBankTitle || 'Disconnect Bank'}
      icon={<AlertTriangle size={22} />}
      variant="alert"
      maxWidth="xs"
      actions={{
        secondary: {
          label: t.cancel || 'Cancel',
          onClick: () => setUnlinkConfirm((s) => ({ ...s, open: false })),
          variant: 'outlined',
          color: 'secondary',
        },
        primary: {
          label: t.plaidLink?.unlinkBankConfirm || 'Disconnect',
          onClick: () => {
            onUnlinkBank?.(unlinkConfirm.itemId);
            setUnlinkConfirm((s) => ({ ...s, open: false }));
          },
          variant: 'contained',
          color: 'error',
        },
      }}
    >
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.6 }}>
        {t.plaidLink?.unlinkBankConfirmMessage || `Disconnect ${unlinkConfirm.bankName}?`}
      </p>
    </AppModal>
    </>
  );
};

export default Sidebar;
